import React from 'react';
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { 
  Clock, 
  Baby, 
  Cake, 
  DollarSign, 
  Leaf, 
  Crown, 
  ShoppingBag 
} from 'lucide-react';
import { ThemeCategory } from '../../utils/recipeFilters';

const categories = [
  {
    id: 'express' as ThemeCategory,
    title: 'Recettes Express',
    titleArabic: 'وصفات سريعة',
    description: 'Prêt en moins de 15 minutes !',
    icon: Clock,
    color: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },

   {
    id: 'traditionnelles' as ThemeCategory,
    title: 'Recettes Traditionnelles',
    titleArabic: 'وصفات تقليدية',
    description: 'Les classiques de nos grands-mères !',
    icon: Crown,
    color: 'bg-amber-100',
    iconColor: 'text-amber-600'
  },
  
  {
    id: 'enfants' as ThemeCategory,
    title: 'Recettes Enfants',
    titleArabic: 'وصفات الأطفال',
    description: 'Des plats que les petits adorent !',
    icon: Baby,
    color: 'bg-pink-100',
    iconColor: 'text-pink-600'
  },
  {
    id: 'sucrees' as ThemeCategory,
    title: 'Recettes Sucrées',
    titleArabic: 'وصفات حلوة',
    description: 'Desserts simples et délicieux !',
    icon: Cake,
    color: 'bg-purple-100',
    iconColor: 'text-purple-600'
  },
  {
    id: 'economiques' as ThemeCategory,
    title: 'Recettes Économiques',
    titleArabic: 'وصفات اقتصادية',
    description: 'Bon marché et savoureux !',
    icon: DollarSign,
    color: 'bg-green-100',
    iconColor: 'text-green-600'
  },
  {
    id: 'vegetariennes' as ThemeCategory,
    title: 'Recettes Végétariennes',
    titleArabic: 'وصفات نباتية',
    description: 'Saveurs vertes et saines !',
    icon: Leaf,
    color: 'bg-emerald-100',
    iconColor: 'text-emerald-600'
  },
 
  {
    id: 'emporter' as ThemeCategory,
    title: 'Recettes à Emporter',
    titleArabic: 'وصفات للسفر',
    description: 'Des pique-niques pour toutes vos envies !',
    icon: ShoppingBag,
    color: 'bg-orange-100',
    iconColor: 'text-orange-600'
  }
];

type Props = {
  onCategorySelect: (theme: ThemeCategory) => void;
};

export default function QaswilaAlaCarte({ onCategorySelect }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalSlides = Math.ceil(categories.length / 2);
  
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1 >= totalSlides ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 < 0 ? totalSlides - 1 : prev - 1));
  };

  // Configuration pour les gestes tactiles
  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false }
  });

  return (
    <section className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Kaswila à la carte</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      <div 
        {...handlers}
        className="relative overflow-hidden rounded-lg"
      >
        <div 
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {Array.from({ length: totalSlides }).map((_, slideIndex) => (
            <div key={slideIndex} className="w-full flex-shrink-0">
              <div className="grid grid-cols-2 gap-4">
                {[0, 1].map((offset) => {
                  const categoryIndex = slideIndex * 2 + offset;
                  if (categoryIndex >= categories.length) return null;
                  
                  const category = categories[categoryIndex];
                  const IconComponent = category.icon;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => onCategorySelect(category.id)}
                      className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] text-left"
                    >
                      <div className={`w-12 h-12 ${category.color} rounded-full flex items-center justify-center mb-3 mx-auto`}>
                        <IconComponent className={`w-6 h-6 ${category.iconColor}`} />
                      </div>
                      
                      <div className="text-center">
                        <h3 className="font-semibold text-gray-800 text-sm mb-1 leading-tight">
                          {category.title}
                        </h3>
                        <p className="text-xs font-bold text-gray-700 mb-1" dir="rtl">
                          {category.titleArabic}
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {category.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Indicateurs de pagination */}
      <div className="flex justify-center mt-4 space-x-2">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              currentIndex === index 
                ? 'bg-orange-500' 
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Indication de swipe pour les utilisateurs */}
      <p className="text-center text-xs text-gray-400 mt-3">
        Glissez pour naviguer
      </p>
    </section>
  );
}